import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useStrings } from '../../i18n';
import { adminAPI } from '../../services/adminService';
import { CartToastContainer, showCartToast } from '../../components/CartToast';

export default function AdminFinance() {
  const strings = useStrings();
  const { getIdToken } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [profitLoss, setProfitLoss] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('success');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0]);
  const [showPLReport] = useState(true);

  const TRANSACTION_TYPES = ['all', 'payment', 'refund', 'adjustment', 'manual'];


  const fetchFinanceData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const idToken = await getIdToken();

      // Fetch transactions
      const txnParams = new URLSearchParams({
        page,
        limit: 10,
        status: statusFilter,
        type: typeFilter !== 'all' ? typeFilter : '',
        dateFrom,
        dateTo
      });

      const txnResponse = await adminAPI.transactions.list(txnParams, idToken);
      if (!txnResponse.ok) throw new Error('Failed to fetch transactions');
      const txnData = await txnResponse.json();
      setTransactions(txnData.transactions);
      setTotalPages(txnData.pagination.pages);

      // Fetch P&L if visible
      if (showPLReport) {
        const plResponse = await adminAPI.profitLoss({ dateFrom, dateTo }, idToken);
        if (!plResponse.ok) throw new Error('Failed to fetch P&L report');
        const plData = await plResponse.json();
        setProfitLoss(plData);
      }
    } catch (err) {
      setError(err.message);
      showCartToast(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [getIdToken, page, statusFilter, typeFilter, dateFrom, dateTo, showPLReport]);

  useEffect(() => {
    fetchFinanceData();
  }, [fetchFinanceData]);

  const getStatusColor = (status) => {
    switch(status) {
      case 'success': return 'bg-green-600';
      case 'pending': return 'bg-yellow-600';
      case 'failed': return 'bg-red-600';
      default: return 'bg-gray-600';
    }
  };

  return (
    <div className="w-full bg-primaryBg min-h-screen">
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-20 fade-section show">
        {/* Back Button */}
        <Link to="/admin" className="inline-flex items-center text-secondary hover:text-secondary/80 mb-6 transition-colors">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {strings.admin?.backToDashboard || 'Back to Dashboard'}
        </Link>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-heading text-headingText mb-2">
            {strings.admin?.finance || 'Finance'}
          </h1>
          <p className="text-bodyText">
            {strings.admin?.viewFinancialData || 'Track transactions and financial reports'}
          </p>
        </div>

        {/* P&L Report */}
        {showPLReport && profitLoss && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {/* Total Revenue */}
            <div className="bg-cardBg rounded-2xl shadow-card p-6 border border-green-200">
              <p className="text-textSecondary text-sm mb-2">{strings.admin?.totalRevenue || 'Total Revenue'}</p>
              <p className="text-3xl font-heading text-headingText">
                ₹{profitLoss.summary.totalRevenue?.toLocaleString('en-IN') || '0'}
              </p>
              <p className="text-textSecondary text-xs mt-2">
                {dateFrom} to {dateTo}
              </p>
            </div>

            {/* Net Revenue */}
            <div className="bg-cardBg rounded-2xl shadow-card p-6 border border-blue-200">
              <p className="text-textSecondary text-sm mb-2">{strings.admin?.netRevenue || 'Net Revenue'}</p>
              <p className="text-3xl font-heading text-headingText">
                ₹{profitLoss.summary.netRevenue?.toLocaleString('en-IN') || '0'}
              </p>
              <p className="text-textSecondary text-xs mt-2">
                {strings.admin?.afterRefunds || 'After refunds'}
              </p>
            </div>

            {/* Gross Profit */}
            <div className="bg-cardBg rounded-2xl shadow-card p-6 border border-purple-200">
              <p className="text-textSecondary text-sm mb-2">{strings.admin?.grossProfit || 'Gross Profit'}</p>
              <p className="text-3xl font-heading text-headingText">
                ₹{profitLoss.summary.grossProfit?.toLocaleString('en-IN') || '0'}
              </p>
              <p className="text-textSecondary text-xs mt-2">
                {profitLoss.summary.profitMargin?.toFixed(1) || '0'}% {strings.admin?.margin || 'margin'}
              </p>
            </div>

            {/* Net Profit */}
            <div className="bg-cardBg rounded-2xl shadow-card p-6 border border-secondary/20">
              <p className="text-textSecondary text-sm mb-2">{strings.admin?.netProfit || 'Net Profit'}</p>
              <p className="text-3xl font-heading text-secondary">
                ₹{profitLoss.summary.netProfit?.toLocaleString('en-IN') || '0'}
              </p>
              <p className="text-textSecondary text-xs mt-2">
                {profitLoss.summary.roi?.toFixed(1) || '0'}% {strings.admin?.roi || 'ROI'}
              </p>
            </div>
          </div>
        )}

        {/* Top Products by Revenue */}
        {profitLoss && profitLoss.topProducts && profitLoss.topProducts.length > 0 && (
          <div className="bg-cardBg rounded-2xl shadow-card p-8 border border-secondary/10 mb-12">
            <h2 className="text-2xl font-heading text-headingText mb-6">
              {strings.admin?.topProductsByRevenue || 'Top Products by Revenue'}
            </h2>
            <div className="space-y-4">
              {profitLoss.topProducts.map((product, idx) => (
                <div key={idx} className="flex items-center justify-between pb-4 border-b border-secondary/10 last:border-0">
                  <div className="flex-1">
                    <p className="font-medium text-headingText">{product.name}</p>
                    <p className="text-sm text-textSecondary">
                      {product.unitsSold} {strings.admin?.unitsSold || 'units sold'} • {product.profitMargin?.toFixed(1)}% {strings.admin?.margin || 'margin'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-headingText">₹{product.revenue?.toLocaleString('en-IN') || '0'}</p>
                    <p className="text-sm text-secondary">+₹{product.profit?.toLocaleString('en-IN') || '0'} profit</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Date Range & Filters */}
        <div className="mb-8 p-6 bg-cardBg rounded-2xl shadow-card border border-secondary/10">
          <h3 className="text-lg font-heading text-headingText mb-4">{strings.admin?.transactionFilters || 'Transaction Filters'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-headingText mb-2">
                {strings.admin?.dateFrom || 'From Date'}
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setPage(1);
                }}
                className="w-full px-4 py-3 border border-secondary/20 rounded-lg bg-surface text-headingText focus:outline-none focus:border-secondary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-headingText mb-2">
                {strings.admin?.dateTo || 'To Date'}
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setPage(1);
                }}
                className="w-full px-4 py-3 border border-secondary/20 rounded-lg bg-surface text-headingText focus:outline-none focus:border-secondary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-headingText mb-2">
                {strings.admin?.status || 'Status'}
              </label>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                className="w-full px-4 py-3 border border-secondary/20 rounded-lg bg-surface text-headingText focus:outline-none focus:border-secondary"
              >
                <option value="success">{strings.admin?.successful || 'Successful'}</option>
                <option value="pending">{strings.admin?.pending || 'Pending'}</option>
                <option value="failed">{strings.admin?.failed || 'Failed'}</option>
                <option value="all">{strings.admin?.allStatus || 'All'}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-headingText mb-2">
                {strings.admin?.type || 'Type'}
              </label>
              <select
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value);
                  setPage(1);
                }}
                className="w-full px-4 py-3 border border-secondary/20 rounded-lg bg-surface text-headingText focus:outline-none focus:border-secondary"
              >
                {TRANSACTION_TYPES.map(type => (
                  <option key={type} value={type}>
                    {type === 'all' ? 'All Types' : type.charAt(0).toUpperCase() + type.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Transactions Table */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-bodyText">{strings.admin?.loading || 'Loading...'}</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
            {error}
          </div>
        ) : transactions.length === 0 ? (
          <div className="bg-cardBg rounded-2xl shadow-card p-12 text-center border border-secondary/10">
            <p className="text-bodyText text-lg">{strings.admin?.noTransactions || 'No transactions found'}</p>
          </div>
        ) : (
          <div className="bg-cardBg rounded-2xl shadow-card overflow-hidden border border-secondary/10">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface border-b border-secondary/20">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-heading text-headingText">{strings.admin?.date || 'Date'}</th>
                    <th className="px-6 py-4 text-left text-sm font-heading text-headingText">{strings.admin?.type || 'Type'}</th>
                    <th className="px-6 py-4 text-left text-sm font-heading text-headingText">{strings.admin?.description || 'Description'}</th>
                    <th className="px-6 py-4 text-left text-sm font-heading text-headingText">{strings.admin?.amount || 'Amount'}</th>
                    <th className="px-6 py-4 text-left text-sm font-heading text-headingText">{strings.admin?.status || 'Status'}</th>
                    <th className="px-6 py-4 text-left text-sm font-heading text-headingText">{strings.admin?.reconciled || 'Reconciled'}</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((txn) => (
                    <tr key={txn._id} className="border-b border-secondary/10 hover:bg-surface transition-colors duration-200">
                      <td className="px-6 py-4 text-sm text-bodyText">
                        {new Date(txn.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-sm text-bodyText capitalize">
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {txn.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-bodyText">{txn.description}</td>
                      <td className="px-6 py-4 font-medium text-headingText">
                        {txn.type === 'refund' ? '-' : '+'}₹{txn.amount.toLocaleString('en-IN')}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium text-white ${getStatusColor(txn.status)}`}>
                          {txn.status.charAt(0).toUpperCase() + txn.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium text-white ${
                          txn.reconciled ? 'bg-green-600' : 'bg-yellow-600'
                        }`}>
                          {txn.reconciled ? 'Yes' : 'Pending'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 bg-surface border-t border-secondary/20 flex items-center justify-center gap-4">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 text-sm text-bodyText hover:text-secondary disabled:opacity-50"
                >
                  ← {strings.admin?.previous || 'Previous'}
                </button>
                <span className="text-sm text-bodyText">
                  {strings.admin?.page || 'Page'} {page} {strings.admin?.of || 'of'} {totalPages}
                </span>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 text-sm text-bodyText hover:text-secondary disabled:opacity-50"
                >
                  {strings.admin?.next || 'Next'} →
                </button>
              </div>
            )}
          </div>
        )}

      <CartToastContainer />
      </div>
    </div>
  );
}
